import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  Avatar, 
  Menu, 
  MenuItem, 
  Grid,
  TextField,
  InputAdornment,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Modal,
  Button,
  Fade,
  Backdrop,
  Divider,
  Alert,
  Tooltip,
  LinearProgress,
  ListItemText,
  ListItemIcon,
  Tabs,
  Tab,
  Chip,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SyncIcon from '@mui/icons-material/Sync';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import SuperAdminLayout from './SuperAdminLayout';
import CompanyCard from './CompanyCard';
import SuperAdminService, { AiChatCompanyRow, FiscalDevice, DealerRow } from '../../services/SuperAdminService';
import LiveSupportPanel from './LiveSupportPanel';
import AiChatSettingsDialog from './AiChatSettingsDialog';
import ModuleSettingsDialog from './ModuleSettingsDialog';
import CompanyDetailsModal from './CompanyDetailsModal';
import FiscalDeviceModal from './FiscalDeviceModal';
import CreateCompanyDialog from './CreateCompanyDialog';

interface Company {
  id: string;
  name: string;
  logo?: string;
  admin: {
    name: string;
    email: string;
    phone?: string;
  };
  maxPersonnel?: number;
  createdAt?: string;
  /** Şirket bir bayiye atanmışsa (backend GET companies yanıtından). */
  dealerId?: number | null;
  dealerName?: string | null;
  /** Demo bitiş tarihi (ISO); null = demo değil. demoExpired sunucuda hesaplanır. */
  demoUntil?: string | null;
  demoExpired?: boolean;
}

// Detaylı şirket bilgileri için ayrı interface (modal için)
interface CompanyWithStats extends Company {
  stats: {
    personnelCount: number;
    branchCount: number;
    activeTasks: number;
    completedTasks: number;
    shifts: number;
    surveys: number;
  };
}

const ModalStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 500 },
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 0,
  outline: 'none',
  overflow: 'hidden'
};

const SuperAdmin: React.FC = () => {
  const { currentUser, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');
  const [apiWarning, setApiWarning] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [newMaxPersonnel, setNewMaxPersonnel] = useState<number>(0);
  const [updating, setUpdating] = useState(false);
  
  // Şirket silme ile ilgili state'ler
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<{id: string, name: string} | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState<string[]>([]);
  
  // Şirket detayları modalı için state'ler
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [detailsCompany, setDetailsCompany] = useState<Company | null>(null);

  // SQL'e Senkronize Et — snackbar ve loading state
  const [syncSnackbar, setSyncSnackbar] = useState<{
    open: boolean;
    severity: 'info' | 'success' | 'error';
    message: string;
  }>({ open: false, severity: 'info', message: '' });
  const [syncingCompanyId, setSyncingCompanyId] = useState<string | null>(null);
  
  // Tab state — indeks yerine açık value kullanılır (rol koşullu sekmelerde indeks kayar).
  // 'demo' her iki rolde de görünür; diğer özel sekmeler yalnız superadmin.
  type TabValue = 'all' | 'demo' | 'active' | 'contracts' | 'fiscal-companies' | 'fiscal-devices';
  const [currentTab, setCurrentTab] = useState<TabValue>('all');
  
  // Aktif şirketler için state'ler
  const [activeCompanies, setActiveCompanies] = useState<Company[]>([]);
  const [activeCompaniesLoading, setActiveCompaniesLoading] = useState(false);
  const [loginActivities, setLoginActivities] = useState<Record<string, any>>({});
  
  // Sözleşme yönetimi için state'ler
  const [contracts, setContracts] = useState<Record<string, any>>({});
  const [isContractModalOpen, setIsContractModalOpen] = useState(false);
  const [selectedCompanyForContract, setSelectedCompanyForContract] = useState<Company | null>(null);
  const [contractStartDate, setContractStartDate] = useState('');
  const [contractEndDate, setContractEndDate] = useState('');
  const [savingContract, setSavingContract] = useState(false);
  
  // AI chat asistanı yönetimi (şirket bazlı aç/kapat + limit + kullanım)
  const [aiChatRows, setAiChatRows] = useState<Record<string, AiChatCompanyRow>>({});
  const [aiChatCompany, setAiChatCompany] = useState<{ id: string; name: string } | null>(null);

  // Modül yönetimi diyaloğu (şirket bazlı modül aç/kapa)
  const [modulesCompany, setModulesCompany] = useState<{ id: string; name: string } | null>(null);

  // Şirket oluşturma diyaloğu (superadmin + bayi — bayi şirketi otomatik kendine atanır)
  const [isCreateCompanyOpen, setIsCreateCompanyOpen] = useState(false);

  // "Bayiye Ata" kısayolu (yalnız süper admin) — kart menüsünden açılır
  const [assignDealerCompany, setAssignDealerCompany] = useState<Company | null>(null);
  const [dealersList, setDealersList] = useState<DealerRow[]>([]);
  const [dealersLoading, setDealersLoading] = useState(false);
  const [selectedDealer, setSelectedDealer] = useState<DealerRow | null>(null);
  const [assigningDealer, setAssigningDealer] = useState(false);

  // Şirket listesini sessizce yeniden yükler (oluşturma/atama sonrası).
  const reloadCompanies = React.useCallback(async () => {
    try {
      const result = await SuperAdminService.getInstance().getAllCompanies();
      setCompanies(result);
    } catch (error) {
      console.error('❌ [SuperAdmin] Şirket listesi yenilenirken hata:', error);
    }
  }, []);

  // Yazarkasa cihazları için state'ler
  const [isFiscalDeviceModalOpen, setIsFiscalDeviceModalOpen] = useState(false);
  const [fiscalDeviceCompany, setFiscalDeviceCompany] = useState<{id: string, name: string} | null>(null);
  const [companiesWithDevices, setCompaniesWithDevices] = useState<string[]>([]);
  const [fiscalDevicesLoading, setFiscalDevicesLoading] = useState(false);
  
  // Tüm yazarkasalar tab'ı için state'ler
  const [allFiscalDevices, setAllFiscalDevices] = useState<FiscalDevice[]>([]);
  const [allFiscalDevicesLoading, setAllFiscalDevicesLoading] = useState(false);
  const [fiscalSearchTerm, setFiscalSearchTerm] = useState('');

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        const startTime = Date.now();
        
        console.log('🚀 [SuperAdmin] Şirketler yükleniyor...');

        // Rol koruması App.tsx'teki RequireRole guard'ında (superadmin|dealer) —
        // sayfa içi ad-hoc kontrol kaldırıldı. Veri backend'den role göre scoped gelir.
        const afterRoleCheck = Date.now();
        
        // API üzerinden şirketleri al
        const apiStartTime = Date.now();
        const superAdminService = SuperAdminService.getInstance();
        const result = await superAdminService.getAllCompanies();
        const apiEndTime = Date.now();
        
        console.log('📊 [SuperAdmin] API Yanıtı:');
        console.log(`   └─ Şirket Sayısı: ${result.length}`);
        console.log(`   └─ API İstek Süresi: ${apiEndTime - apiStartTime}ms (network + backend)`);
        console.log(`   └─ Veri Yapısı: Sadece ID + /info (personnel, tasks, branches YOK)`);
        
        // State güncelleme süresi
        const beforeStateUpdate = Date.now();
        console.log('🔄 [SuperAdmin] State güncelleniyor...');
        
        // Eğer API'den warning gelirse, onu göster
        if (result.length === 0) {
          console.log('⚠️ [SuperAdmin] companies_index tablosu boş!');
          setApiWarning('companies_index tablosu boş. Eski şirketlerin ID\'lerini elle eklemeniz gerekiyor.');
        } else {
          setApiWarning(null);
        }
        
        setCompanies(result);
        
        const afterStateUpdate = Date.now();
        const totalTime = Date.now() - startTime;
        
        console.log('✅ [SuperAdmin] Şirketler başarıyla yüklendi!');
        console.log('⏱️ [SuperAdmin] PERFORMANS ANALİZİ:');
        console.log(`   ├─ Rol Kontrolü: ${afterRoleCheck - startTime}ms (AuthContext'ten)`);
        console.log(`   ├─ API İsteği (network + backend): ${apiEndTime - apiStartTime}ms`);
        console.log(`   ├─ State Güncelleme: ${afterStateUpdate - beforeStateUpdate}ms`);
        console.log(`   └─ TOPLAM: ${totalTime}ms`);
        
      } catch (error) {
        console.error('❌ [SuperAdmin] Şirketler yüklenirken hata:', error);
        setCompanies([]);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchCompanies();
    }
  }, [currentUser, navigate]);

  // Sözleşmeleri yükle
  useEffect(() => {
    const fetchContracts = async () => {
      try {
        const superAdminService = SuperAdminService.getInstance();
        const result = await superAdminService.getAllContracts();
        
        if (result.success) {
          setContracts(result.contracts || {});
          console.log(`✅ [Sözleşmeler] ${result.count} sözleşme yüklendi`);
        }
      } catch (error) {
        console.error('❌ [Sözleşmeler] Yüklenirken hata:', error);
      }
    };

    // Sözleşme uçları süper admine özel (backend SuperAdminOnly) — bayi için çağrılmaz.
    if (currentUser && isSuperAdmin && companies.length > 0) {
      fetchContracts();
    }
  }, [currentUser, isSuperAdmin, companies]);

  // AI chat ayar/kullanım satırlarını yükle (ana GTS DB — SuperAdminAiChatController)
  const fetchAiChatRows = React.useCallback(async () => {
    try {
      const rows = await SuperAdminService.getInstance().getAiChatCompanies();
      const map: Record<string, AiChatCompanyRow> = {};
      rows.forEach((r) => {
        map[r.companyId] = r;
      });
      setAiChatRows(map);
    } catch (error) {
      console.error('❌ [AI Chat] Ayarlar yüklenirken hata:', error);
    }
  }, []);

  useEffect(() => {
    // AI chat yönetimi süper admine özel (backend SuperAdminOnly) — bayi için çağrılmaz.
    if (currentUser && isSuperAdmin && companies.length > 0) {
      fetchAiChatRows();
    }
  }, [currentUser, isSuperAdmin, companies, fetchAiChatRows]);

  const handleViewDetails = (companyId: string) => {
    // Şirket detayları sayfasına yönlendir
    navigate(`/superadmin/company/${companyId}`);
  };

  // "Bayiye Ata" diyaloğunu açar; bayi listesi ilk açılışta yüklenir (yalnız süper admin).
  const handleOpenAssignDealer = async (company: Company) => {
    setAssignDealerCompany(company);
    setSelectedDealer(null);
    if (dealersList.length === 0) {
      try {
        setDealersLoading(true);
        const list = await SuperAdminService.getInstance().getDealers();
        setDealersList(list);
      } catch (error) {
        console.error('❌ [Bayiye Ata] Bayiler yüklenirken hata:', error);
      } finally {
        setDealersLoading(false);
      }
    }
  };

  const closeAssignDealer = () => {
    if (assigningDealer) return;
    setAssignDealerCompany(null);
    setSelectedDealer(null);
  };

  const handleAssignDealer = async () => {
    if (!assignDealerCompany || !selectedDealer || assigningDealer) return;
    try {
      setAssigningDealer(true);
      await SuperAdminService.getInstance().assignCompanyToDealer(
        selectedDealer.id,
        assignDealerCompany.id
      );
      setSyncSnackbar({
        open: true,
        severity: 'success',
        message: `${assignDealerCompany.name} → ${selectedDealer.name} ataması yapıldı.`,
      });
      setAssignDealerCompany(null);
      setSelectedDealer(null);
      await reloadCompanies();
    } catch (error: any) {
      setSyncSnackbar({
        open: true,
        severity: 'error',
        message: error?.message || 'Şirket bayiye atanamadı.',
      });
    } finally {
      setAssigningDealer(false);
    }
  };

  const handleUnassignDealer = async () => {
    if (!assignDealerCompany?.dealerId || assigningDealer) return;
    try {
      setAssigningDealer(true);
      await SuperAdminService.getInstance().unassignCompanyFromDealer(
        assignDealerCompany.dealerId,
        assignDealerCompany.id
      );
      setSyncSnackbar({
        open: true,
        severity: 'success',
        message: `${assignDealerCompany.name} bayi ataması kaldırıldı.`,
      });
      setAssignDealerCompany(null);
      setSelectedDealer(null);
      await reloadCompanies();
    } catch (error: any) {
      setSyncSnackbar({
        open: true,
        severity: 'error',
        message: error?.message || 'Bayi ataması kaldırılamadı.',
      });
    } finally {
      setAssigningDealer(false);
    }
  };

  const handleOpenModal = (company: Company) => {
    setSelectedCompany(company);
    setNewMaxPersonnel(company.maxPersonnel || 0);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCompany(null);
  };

  const handleUpdateMaxPersonnel = async () => {
    if (!selectedCompany) return;
    
    try {
      setUpdating(true);
      
      // API üzerinden güncelle
      const superAdminService = SuperAdminService.getInstance();
      await superAdminService.updateMaxPersonnel(selectedCompany.id, newMaxPersonnel);
      
      // Yerel state'i güncelle
      setCompanies(prevCompanies => 
        prevCompanies.map(company => 
          company.id === selectedCompany.id 
            ? { ...company, maxPersonnel: newMaxPersonnel } 
            : company
        )
      );
      
      handleCloseModal();
    } catch (error) {
      console.error('Personel sayısı güncellenirken hata:', error);
      alert('Personel sayısı güncellenirken bir hata oluştu');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteCompany = (companyId: string, companyName: string) => {
    setCompanyToDelete({ id: companyId, name: companyName });
    setIsDeleteModalOpen(true);
    setDeleteConfirmation('');
    setDeleteProgress([]);
  };

  // Firebase'deki firma bilgilerini SQL Companies tablosuna senkronize et
  const handleSyncCompanyToSql = async (companyId: string, companyName: string) => {
    if (syncingCompanyId === companyId) return;
    try {
      setSyncingCompanyId(companyId);
      setSyncSnackbar({
        open: true,
        severity: 'info',
        message: `${companyName} senkronize ediliyor...`,
      });
      const superAdminService = SuperAdminService.getInstance();
      const result = await superAdminService.syncCompanyToSql(companyId);
      setSyncSnackbar({
        open: true,
        severity: 'success',
        message: `${companyName} SQL'e senkronize edildi (sqlId=${result.sqlId})`,
      });
    } catch (err: any) {
      console.error('SQL senkronizasyon hatasi:', err);
      const apiMsg = err?.response?.data?.message || err?.message || 'Bilinmeyen hata';
      setSyncSnackbar({
        open: true,
        severity: 'error',
        message: `Senkronize edilemedi: ${apiMsg}`,
      });
    } finally {
      setSyncingCompanyId(null);
    }
  };
  
  // Şirketin sözleşmesi var mı kontrol et
  const hasActiveContract = (companyId: string): boolean => {
    const contract = contracts[companyId];
    return contract !== undefined && contract !== null;
  };

  const closeDeleteModal = () => {
    if (!isDeleting) {
      setIsDeleteModalOpen(false);
      setCompanyToDelete(null);
      setDeleteConfirmation('');
      setDeleteProgress([]);
    }
  };

  const confirmDelete = async () => {
    console.log('🗑️ [Delete] confirmDelete çağrıldı');
    console.log('🗑️ [Delete] companyToDelete:', companyToDelete);
    console.log('🗑️ [Delete] deleteConfirmation:', deleteConfirmation);
    
    if (!companyToDelete) {
      console.error('❌ [Delete] companyToDelete null!');
      return;
    }
    
    if (deleteConfirmation !== companyToDelete.name) {
      console.error('❌ [Delete] Şirket adı eşleşmiyor!', {
        expected: companyToDelete.name,
        received: deleteConfirmation
      });
      return;
    }

    try {
      console.log('✅ [Delete] Silme işlemi başlatılıyor...');
      setIsDeleting(true);
      setDeleteProgress([`Şirket silme işlemi başlatılıyor: ${companyToDelete.name} (${companyToDelete.id})...`]);

      // API üzerinden şirketi sil
      const superAdminService = SuperAdminService.getInstance();
      console.log('🔄 [Delete] API çağrısı yapılıyor:', companyToDelete.id);
      
      const result = await superAdminService.deleteCompany(companyToDelete.id);
      console.log('✅ [Delete] API yanıtı alındı:', result);
      
      // API'den dönen logları ekle
      setDeleteProgress(prev => [...prev, ...result.log]);
      
      // Companies listesini güncelle
      setCompanies(prevCompanies => prevCompanies.filter(company => company.id !== companyToDelete.id));
      
      // İşlem tamamlandı
      setDeleteProgress(prev => [...prev, `İşlem tamamlandı.`]);
      
      console.log('✅ [Delete] Silme işlemi tamamlandı, 5 saniye sonra modal kapanacak');
      
      // 5 saniye sonra modalı kapat
      setTimeout(() => {
        setIsDeleting(false);
        setIsDeleteModalOpen(false);
        setCompanyToDelete(null);
        setDeleteConfirmation('');
        setDeleteProgress([]);
      }, 5000);
      
    } catch (error: any) {
      console.error('❌ [Delete] Şirket silinirken hata:', error);
      console.error('❌ [Delete] Hata detayı:', {
        message: error?.message,
        response: error?.response,
        stack: error?.stack
      });
      setDeleteProgress(prev => [...prev, `Kritik hata: ${error?.message || error}`]);
      setIsDeleting(false);
    }
  };

  // Tab değiştir
  const handleTabChange = (event: React.SyntheticEvent, newValue: TabValue) => {
    setCurrentTab(newValue);
    
    // Aktif Şirketler sekmesine geçildiğinde verileri yükle
    if (newValue === 'active' && activeCompanies.length === 0) {
      loadActiveCompanies();
    }
    
    // Yazarkasa Verilen Şirketler sekmesine geçildiğinde verileri yükle
    if (newValue === 'fiscal-companies' && companiesWithDevices.length === 0) {
      loadCompaniesWithFiscalDevices();
    }
    
    // Yazarkasalar sekmesine geçildiğinde tüm cihazları yükle
    if (newValue === 'fiscal-devices' && allFiscalDevices.length === 0) {
      loadAllFiscalDevices();
    }
  };

  // Aktif şirketleri yükle (Son 7 günde giriş yapmış)
  const loadActiveCompanies = async () => {
    if (activeCompaniesLoading) return;
    
    try {
      setActiveCompaniesLoading(true);
      console.log(`🔍 [Aktif Şirketler] Login activities verisi okunuyor...`);
      
      // API üzerinden login activities al
      const superAdminService = SuperAdminService.getInstance();
      const result = await superAdminService.getRecentLoginActivities();
      
      if (result.success && result.data) {
        const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
        const activeCompanyIds = result.data
          .filter((activity: any) => activity.lastLogin >= sevenDaysAgo)
          .map((activity: any) => activity.companyId);
        
        console.log(`✅ [Aktif Şirketler] ${activeCompanyIds.length} aktif şirket bulundu (son 7 gün)`);
        
        // Aktif şirketlerin bilgilerini al
        const activeCompaniesList = companies.filter(company => 
          activeCompanyIds.includes(company.id)
        );
        
        // Login activities'i kaydet
        const activitiesMap: Record<string, any> = {};
        result.data.forEach((activity: any) => {
          activitiesMap[activity.companyId] = activity;
        });
        
        setLoginActivities(activitiesMap);
        setActiveCompanies(activeCompaniesList);
        
      } else {
        console.log('⚠️ [Aktif Şirketler] Login activities verisi bulunamadı');
        setActiveCompanies([]);
      }
      
    } catch (error) {
      console.error('❌ [Aktif Şirketler] Yüklenirken hata:', error);
      alert('Aktif şirketler yüklenirken bir hata oluştu');
    } finally {
      setActiveCompaniesLoading(false);
    }
  };

  // Yazarkasa cihazı olan şirketleri yükle
  const loadCompaniesWithFiscalDevices = async () => {
    if (fiscalDevicesLoading) return;
    
    try {
      setFiscalDevicesLoading(true);
      const superAdminService = SuperAdminService.getInstance();
      const companyIds = await superAdminService.getCompaniesWithFiscalDevices();
      setCompaniesWithDevices(companyIds);
      console.log(`✅ [Yazarkasa] ${companyIds.length} şirkette cihaz bulundu`);
    } catch (error) {
      console.error('❌ [Yazarkasa] Şirketler yüklenirken hata:', error);
    } finally {
      setFiscalDevicesLoading(false);
    }
  };

  // Yazarkasa cihazları modalını aç
  const handleOpenFiscalDeviceModal = (company: Company) => {
    setFiscalDeviceCompany({ id: company.id, name: company.name });
    setIsFiscalDeviceModalOpen(true);
  };

  // Yazarkasa cihazları modalını kapat
  const handleCloseFiscalDeviceModal = () => {
    setIsFiscalDeviceModalOpen(false);
    setFiscalDeviceCompany(null);
    // Yazarkasa Verilen Şirketler sekmesi aktifse listeyi yeniden yükle
    if (currentTab === 'fiscal-companies') {
      loadCompaniesWithFiscalDevices();
    }
  };

  // Tüm yazarkasa cihazlarını yükle
  const loadAllFiscalDevices = async () => {
    if (allFiscalDevicesLoading) return;
    try {
      setAllFiscalDevicesLoading(true);
      const superAdminService = SuperAdminService.getInstance();
      const devices = await superAdminService.getAllFiscalDevices();
      setAllFiscalDevices(devices);
      console.log(`✅ [Yazarkasalar] ${devices.length} cihaz yüklendi`);
    } catch (error) {
      console.error('❌ [Yazarkasalar] Cihazlar yüklenirken hata:', error);
    } finally {
      setAllFiscalDevicesLoading(false);
    }
  };

  // Kalan gün hesapla
  const getDaysRemaining = (expiryDate: string | null): number | null => {
    if (!expiryDate) return null;
    const now = new Date();
    const expiry = new Date(expiryDate);
    return Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  };

  // Tarih formatla
  const formatDateTR = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    try { return new Date(dateStr).toLocaleDateString('tr-TR'); } catch { return '-'; }
  };

  // CompanyId'den şirket adı bul
  const getCompanyName = (companyId: string): string => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || companyId;
  };

  // Yazarkasalar tablosu için filtreleme
  const filteredFiscalDevices = allFiscalDevices.filter(device => {
    if (!fiscalSearchTerm) return true;
    const term = fiscalSearchTerm.toLowerCase();
    const companyName = getCompanyName(device.companyId).toLowerCase();
    return (
      companyName.includes(term) ||
      (device.brand || '').toLowerCase().includes(term) ||
      (device.model || '').toLowerCase().includes(term) ||
      (device.serialNumber || '').toLowerCase().includes(term) ||
      (device.city || '').toLowerCase().includes(term) ||
      (device.branch || '').toLowerCase().includes(term) ||
      (device.usageArea || '').toLowerCase().includes(term) ||
      (device.authorizedPerson || '').toLowerCase().includes(term) ||
      (device.vkn || '').toLowerCase().includes(term)
    );
  });

  // Şirketleri filtrele (arama ve tab'a göre)
  const filteredCompanies = (() => {
    if (currentTab === 'all') {
      // Tüm Şirketler
      return companies.filter(company => {
        if (!company || !company.name) return false;
        return company.name.toLowerCase().includes(searchTerm.toLowerCase());
      });
    } else if (currentTab === 'demo') {
      // Demo Şirketler — DemoUntil dolu olanlar (süresi geçenler dahil)
      return companies.filter(company => {
        if (!company || !company.name) return false;
        return !!company.demoUntil && company.name.toLowerCase().includes(searchTerm.toLowerCase());
      });
    } else if (currentTab === 'active') {
      // Aktif Şirketler - tarama yapıldıysa activeCompanies'ten göster
      return activeCompanies.filter(company => {
        if (!company || !company.name) return false;
        return company.name.toLowerCase().includes(searchTerm.toLowerCase());
      });
    } else if (currentTab === 'contracts') {
      // Sözleşmeli Şirketler - sözleşmesi olan şirketleri göster
      return companies.filter(company => {
        if (!company || !company.name) return false;
        const hasContract = contracts[company.id] !== undefined && contracts[company.id] !== null;
        return hasContract && company.name.toLowerCase().includes(searchTerm.toLowerCase());
      });
    } else if (currentTab === 'fiscal-companies') {
      // Yazarkasa Verilen Şirketler
      return companies.filter(company => {
        if (!company || !company.name) return false;
        const hasDevice = companiesWithDevices.includes(company.id);
        return hasDevice && company.name.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }
    return [];
  })();

  // Şirket detayları modalını açma
  const handleOpenDetailsModal = (company: Company) => {
    setDetailsCompany(company);
    setIsDetailsModalOpen(true);
  };

  // Şirket detayları modalını kapatma
  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setDetailsCompany(null);
  };

  // Liste görünümü için menu state
  const [tableMenuAnchorEl, setTableMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTableCompany, setSelectedTableCompany] = useState<Company | null>(null);

  const handleTableMenuClick = (event: React.MouseEvent<HTMLElement>, company: Company) => {
    event.stopPropagation();
    setTableMenuAnchorEl(event.currentTarget);
    setSelectedTableCompany(company);
  };

  const handleTableMenuClose = () => {
    setTableMenuAnchorEl(null);
    setSelectedTableCompany(null);
  };

  const handleTableMenuEdit = () => {
    if (selectedTableCompany) {
      handleOpenModal(selectedTableCompany);
    }
    handleTableMenuClose();
  };

  const handleTableMenuDelete = () => {
    if (selectedTableCompany) {
      handleDeleteCompany(selectedTableCompany.id, selectedTableCompany.name);
    }
    handleTableMenuClose();
  };

  const handleTableMenuFiscalDevices = () => {
    if (selectedTableCompany) {
      handleOpenFiscalDeviceModal(selectedTableCompany);
    }
    handleTableMenuClose();
  };

  const handleTableMenuSyncToSql = () => {
    if (selectedTableCompany) {
      handleSyncCompanyToSql(selectedTableCompany.id, selectedTableCompany.name);
    }
    handleTableMenuClose();
  };

  // Liste görünümü bileşeni - sadece info bilgileri
  const CompanyListView = () => (
    <>
      <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 'calc(100vh - 200px)', overflow: 'auto', overflowX: 'auto' }}>
        <Table stickyHeader sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell>Şirket</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Admin</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Telefon</TableCell>
              <TableCell>Kayıt Tarihi</TableCell>
              <TableCell>Max Personel</TableCell>
              <TableCell align="center">İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCompanies.map((company) => (
              <TableRow 
                key={company.id} 
                sx={{ 
                  '&:hover': { bgcolor: '#f9f9f9' }
                }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar
                      src={company.logo}
                      alt={company.name}
                      sx={{ width: 40, height: 40, bgcolor: '#1976d2' }}
                    >
                      {company.name.charAt(0)}
                    </Avatar>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      {company.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontFamily: 'monospace', 
                      fontSize: '0.75rem',
                      bgcolor: 'rgba(0,0,0,0.05)',
                      p: 0.5,
                      borderRadius: 1,
                      display: 'inline-block'
                    }}
                  >
                    {company.id}
                  </Typography>
                </TableCell>
                <TableCell>{company.admin.name}</TableCell>
                <TableCell>{company.admin.email}</TableCell>
                <TableCell>{company.admin.phone || '-'}</TableCell>
                <TableCell>{company.createdAt ? new Date(company.createdAt).toLocaleDateString('tr-TR') : '-'}</TableCell>
                <TableCell>{company.maxPersonnel || 0}</TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={(e) => handleTableMenuClick(e, company)}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Tablo Menu */}
      <Menu
        anchorEl={tableMenuAnchorEl}
        open={Boolean(tableMenuAnchorEl)}
        onClose={handleTableMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleTableMenuEdit}>
          <ListItemIcon>
            <PeopleAltIcon fontSize="small" sx={{ color: '#1976d2' }} />
          </ListItemIcon>
          <ListItemText>Personel Sayısını Güncelle</ListItemText>
        </MenuItem>
        {/* Süper admine özel aksiyonlar — bayi görünümünde gizli */}
        {isSuperAdmin && (
          <MenuItem onClick={handleTableMenuFiscalDevices}>
            <ListItemIcon>
              <PointOfSaleIcon fontSize="small" sx={{ color: '#ff9800' }} />
            </ListItemIcon>
            <ListItemText sx={{ color: '#ff9800' }}>Yazarkasa Cihazları</ListItemText>
          </MenuItem>
        )}
        {isSuperAdmin && (
          <MenuItem
            onClick={handleTableMenuSyncToSql}
            disabled={!!syncingCompanyId && syncingCompanyId === selectedTableCompany?.id}
          >
            <ListItemIcon>
              {syncingCompanyId && syncingCompanyId === selectedTableCompany?.id ? (
                <CircularProgress size={16} sx={{ color: '#1976d2' }} />
              ) : (
                <CloudUploadIcon fontSize="small" sx={{ color: '#1976d2' }} />
              )}
            </ListItemIcon>
            <ListItemText sx={{ color: '#1976d2' }}>SQL'e Senkronize Et</ListItemText>
          </MenuItem>
        )}
        {isSuperAdmin && (
          <MenuItem onClick={handleTableMenuDelete}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" sx={{ color: '#f44336' }} />
            </ListItemIcon>
            <ListItemText sx={{ color: '#f44336' }}>Şirketi Sil</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );

  // Sözleşme modalını aç
  const handleOpenContractModal = (company: Company) => {
    setSelectedCompanyForContract(company);
    
    // Eğer şirketin mevcut bir sözleşmesi varsa, tarihleri doldur
    const existingContract = contracts[company.id];
    if (existingContract) {
      setContractStartDate(new Date(existingContract.startDate).toISOString().split('T')[0]);
      setContractEndDate(new Date(existingContract.endDate).toISOString().split('T')[0]);
    } else {
      setContractStartDate('');
      setContractEndDate('');
    }
    
    setIsContractModalOpen(true);
  };

  // Sözleşme modalını kapat
  const handleCloseContractModal = () => {
    setIsContractModalOpen(false);
    setSelectedCompanyForContract(null);
    setContractStartDate('');
    setContractEndDate('');
  };

  // Sözleşme kaydet
  const handleSaveContract = async () => {
    if (!selectedCompanyForContract || !contractStartDate || !contractEndDate) {
      alert('Lütfen tüm alanları doldurun');
      return;
    }

    try {
      setSavingContract(true);
      
      const superAdminService = SuperAdminService.getInstance();
      const result = await superAdminService.createCompanyContract(
        selectedCompanyForContract.id,
        contractStartDate,
        contractEndDate
      );

      if (result.success) {
        // Sözleşmeleri yeniden yükle
        const contractsResult = await superAdminService.getAllContracts();
        if (contractsResult.success) {
          setContracts(contractsResult.contracts || {});
        }

        handleCloseContractModal();
        alert('Sözleşme başarıyla kaydedildi!');
      }
    } catch (error) {
      console.error('Sözleşme kaydedilirken hata:', error);
      alert('Sözleşme kaydedilirken bir hata oluştu');
    } finally {
      setSavingContract(false);
    }
  };

  return (
    <SuperAdminLayout title="GTS Süper Admin" icon={<BusinessIcon />}>
          <Box sx={{ 
            mb: 4, 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
                <TextField
                  variant="outlined"
                  placeholder="Şirket ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: { xs: '100%', sm: 'auto' },
                    maxWidth: 400,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'background.paper'
                    }
                  }}
                />
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  bgcolor: '#1976d2', 
                  color: 'white',
                  px: 2,
                  py: 1.5,
                  borderRadius: 2,
                  minWidth: { xs: '100%', sm: 140 },
                  boxSizing: 'border-box'
                }}>
                  <BusinessIcon fontSize="small" />
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    Toplam Şirket: {filteredCompanies.length}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<AddBusinessIcon />}
                  onClick={() => setIsCreateCompanyOpen(true)}
                  sx={{
                    py: 1.4,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    width: { xs: '100%', sm: 'auto' }
                  }}
                >
                  Şirket Oluştur
                </Button>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton 
                color={viewMode === 'card' ? 'primary' : 'default'} 
                onClick={() => setViewMode('card')}
                sx={{ 
                  bgcolor: viewMode === 'card' ? '#1976d2' : 'transparent',
                  color: viewMode === 'card' ? 'white' : 'inherit',
                  '&:hover': { bgcolor: viewMode === 'card' ? '#1565c0' : 'rgba(0,0,0,0.04)' }
                }}
              >
                <ViewModuleIcon />
              </IconButton>
              <IconButton 
                color={viewMode === 'list' ? 'primary' : 'default'} 
                onClick={() => setViewMode('list')}
                sx={{ 
                  bgcolor: viewMode === 'list' ? '#1976d2' : 'transparent',
                  color: viewMode === 'list' ? 'white' : 'inherit',
                  '&:hover': { bgcolor: viewMode === 'list' ? '#1565c0' : 'rgba(0,0,0,0.04)' }
                }}
              >
                <ViewListIcon />
              </IconButton>
            </Box>
          </Box>
          
          {/* Şirket Kategorileri Tab'ları */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs 
              value={currentTab} 
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 500,
                  minHeight: 48,
                },
                '& .Mui-selected': {
                  color: '#1976d2',
                  fontWeight: 600,
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#1976d2',
                  height: 3,
                },
              }}
            >
              <Tab 
                value="all"
                label="Tüm Şirketler"
                icon={<BusinessIcon />}
                iconPosition="start"
              />
              <Tab 
                value="demo"
                label="Demo"
                icon={<HourglassBottomIcon />}
                iconPosition="start"
              />
              {/* Aktif/Sözleşmeli/Yazarkasa sekmeleri süper admine özel uçlara dayanır — bayide gizli */}
              {isSuperAdmin && (
                <Tab 
                  value="active"
                  label="Aktif Şirketler"
                  icon={<BusinessIcon />}
                  iconPosition="start"
                />
              )}
              {isSuperAdmin && (
                <Tab 
                  value="contracts"
                  label="Sözleşmeli Şirketler"
                  icon={<BusinessIcon />}
                  iconPosition="start"
                />
              )}
              {isSuperAdmin && (
                <Tab 
                  value="fiscal-companies"
                  label="Yazarkasa Verilen Şirketler"
                  icon={<PointOfSaleIcon />}
                  iconPosition="start"
                />
              )}
              {isSuperAdmin && (
                <Tab 
                  value="fiscal-devices"
                  label="Yazarkasalar"
                  icon={<PointOfSaleIcon />}
                  iconPosition="start"
                />
              )}
            </Tabs>
          </Box>

          {/* API Uyarı Mesajı */}
          {apiWarning && (
            <Alert severity="warning" sx={{ mb: 3, mt: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                ⚠️ companies_index Tablosu Boş
              </Typography>
              <Typography variant="body2">
                {apiWarning}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Firebase Console'dan manuel olarak şirket ID'lerini ekleyin veya yeni şirket kaydı yapıldığında otomatik olarak eklenecektir.
              </Typography>
            </Alert>
          )}
          
          {currentTab === 'fiscal-devices' ? (
            // Yazarkasalar tab'ı - özel görünüm
            allFiscalDevicesLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>Yazarkasa cihazları yükleniyor...</Typography>
              </Box>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Cihaz, şirket, marka, model ara..."
                    value={fiscalSearchTerm}
                    onChange={(e) => setFiscalSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start"><SearchIcon /></InputAdornment>
                      ),
                    }}
                    sx={{ width: { xs: '100%', sm: 'auto' }, maxWidth: 400, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
                  />
                  <Chip label={`${filteredFiscalDevices.length} Cihaz`} color="primary" />
                  {(() => {
                    const expiredCount = filteredFiscalDevices.filter(d => { const days = getDaysRemaining(d.expiryDate); return days !== null && days < 0; }).length;
                    const warningCount = filteredFiscalDevices.filter(d => { const days = getDaysRemaining(d.expiryDate); return days !== null && days >= 0 && days <= 30; }).length;
                    return (
                      <>
                        {expiredCount > 0 && <Chip label={`${expiredCount} Süresi Dolmuş`} color="error" size="small" />}
                        {warningCount > 0 && <Chip label={`${warningCount} Süresi Yaklaşıyor`} color="warning" size="small" />}
                      </>
                    );
                  })()}
                  <Button variant="outlined" size="small" onClick={loadAllFiscalDevices} startIcon={<SyncIcon />}>Yenile</Button>
                </Box>

                {filteredFiscalDevices.length === 0 ? (
                  <Box sx={{ textAlign: 'center', mt: 8, p: 4 }}>
                    <PointOfSaleIcon sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
                    <Typography variant="h5" sx={{ color: 'text.secondary', mb: 1 }}>
                      {fiscalSearchTerm ? 'Arama Sonucu Bulunamadı' : 'Henüz Yazarkasa Cihazı Yok'}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                      {fiscalSearchTerm ? 'Arama kriterlerinize uygun cihaz bulunamadı.' : 'Şirketlere yazarkasa cihazı eklemek için şirket kartındaki menüyü kullanın.'}
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 280px)', overflow: 'auto', overflowX: 'auto' }}>
                    <Table stickyHeader size="small" sx={{ minWidth: 1400 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: '#f5f5f5' }}>SN</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: '#f5f5f5' }}>Kalan Süre</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: '#f5f5f5' }}>Şirket</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: '#f5f5f5' }}>Marka</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: '#f5f5f5' }}>Model</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: '#f5f5f5' }}>Seri No</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: '#f5f5f5' }}>Aktivasyon</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: '#f5f5f5' }}>Bitiş</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: '#f5f5f5' }}>VKN</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: '#f5f5f5' }}>Yetkili</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: '#f5f5f5' }}>Telefon</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: '#f5f5f5' }}>İl</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: '#f5f5f5' }}>Şube</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: '#f5f5f5' }}>Kullanım Yeri</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: '#f5f5f5' }}>GMÜ Ücret</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: '#f5f5f5' }}>Fatura Durumu</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredFiscalDevices.map((device, index) => {
                          const days = getDaysRemaining(device.expiryDate);
                          const isExpired = days !== null && days < 0;
                          const isWarning = days !== null && days >= 0 && days <= 30;
                          const rowBg = isExpired ? 'rgba(244,67,54,0.08)' : isWarning ? 'rgba(255,152,0,0.08)' : 'inherit';
                          return (
                            <TableRow key={device.id} sx={{ bgcolor: rowBg, '&:hover': { bgcolor: isExpired ? 'rgba(244,67,54,0.14)' : isWarning ? 'rgba(255,152,0,0.14)' : '#f5f5f5' } }}>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {days === null ? (
                                  <Chip label="Belirsiz" size="small" variant="outlined" />
                                ) : isExpired ? (
                                  <Chip label={`${Math.abs(days)} gün geçmiş`} size="small" color="error" />
                                ) : isWarning ? (
                                  <Chip label={`${days} gün`} size="small" color="warning" />
                                ) : (
                                  <Chip label={`${days} gün`} size="small" color="success" variant="outlined" />
                                )}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{getCompanyName(device.companyId)}</TableCell>
                              <TableCell>{device.brand || '-'}</TableCell>
                              <TableCell>{device.model || '-'}</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{device.serialNumber || '-'}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTR(device.activationDate)}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTR(device.expiryDate)}</TableCell>
                              <TableCell>{device.vkn || '-'}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{device.authorizedPerson || '-'}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{device.phone || '-'}</TableCell>
                              <TableCell>{device.city || '-'}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{device.branch || '-'}</TableCell>
                              <TableCell>{device.usageArea || '-'}</TableCell>
                              <TableCell>
                                <Chip
                                  label={device.gmuFeeCollected ? 'Evet' : 'Hayır'}
                                  size="small"
                                  color={device.gmuFeeCollected ? 'success' : 'default'}
                                  variant={device.gmuFeeCollected ? 'filled' : 'outlined'}
                                />
                              </TableCell>
                              <TableCell>
                                <Tooltip title={device.invoiceStatus || ''}>
                                  <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>{device.invoiceStatus || '-'}</Typography>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )
          ) : (loading || (currentTab === 'active' && activeCompaniesLoading) || (currentTab === 'fiscal-companies' && fiscalDevicesLoading)) ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
              <CircularProgress />
              {currentTab === 'active' && activeCompaniesLoading && (
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                  Aktif şirketler yükleniyor...
                </Typography>
              )}
              {currentTab === 'fiscal-companies' && fiscalDevicesLoading && (
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                  Yazarkasa verilen şirketler yükleniyor...
                </Typography>
              )}
            </Box>
          ) : filteredCompanies.length === 0 ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center',
              mt: 8,
              p: 4
            }}>
              <BusinessIcon sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
              <Typography variant="h5" sx={{ mb: 1, color: 'text.secondary' }}>
                {currentTab === 'active' 
                  ? 'Aktif Şirket Bulunamadı'
                  : currentTab === 'contracts'
                  ? 'Sözleşmeli Şirket Bulunamadı'
                  : currentTab === 'fiscal-companies'
                  ? 'Yazarkasa Verilen Şirket Bulunamadı'
                  : currentTab === 'demo'
                  ? 'Demo Şirket Bulunamadı'
                  : 'Şirket Bulunamadı'
                }
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                {currentTab === 'active' 
                  ? 'Son 7 günde giriş yapan şirket bulunamadı.'
                  : currentTab === 'contracts'
                  ? (searchTerm
                      ? 'Arama kriterlerinize uygun sözleşmeli şirket bulunamadı.'
                      : 'Henüz sözleşme yapılmış şirket bulunmuyor.'
                    )
                  : currentTab === 'fiscal-companies'
                  ? (searchTerm
                      ? 'Arama kriterlerinize uygun yazarkasa verilen şirket bulunamadı.'
                      : 'Henüz yazarkasa cihazı verilmiş şirket bulunmuyor.'
                    )
                  : currentTab === 'demo'
                  ? (searchTerm
                      ? 'Arama kriterlerinize uygun demo şirket bulunamadı.'
                      : 'Henüz demo süresi tanımlanmış şirket bulunmuyor. Şirket kartındaki "Modüller" diyaloğundan demo süresi verebilirsiniz.'
                    )
                  : searchTerm 
                    ? 'Arama kriterlerinize uygun şirket bulunamadı.' 
                    : 'Sistemde kayıtlı şirket bulunmuyor.'
                }
              </Typography>
              {!searchTerm && apiWarning && currentTab === 'all' && (
                <Typography variant="body2" sx={{ mt: 2, color: 'warning.main', textAlign: 'center' }}>
                  Yeni şirket kaydı yapın veya mevcut şirketlerin ID'lerini companies_index tablosuna ekleyin.
                </Typography>
              )}
            </Box>
          ) : viewMode === 'card' ? (
            <Box sx={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto', pb: 8 }}>
              <Grid container spacing={2}>
                {filteredCompanies.map((company) => (
                  <Grid 
                    item 
                    xs={12} 
                    sm={6} 
                    md={4} 
                    lg={3} 
                    xl={2} 
                    key={company.id}
                    onClick={() => handleOpenDetailsModal(company)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <CompanyCard
                      {...company}
                      contract={contracts[company.id] || null}
                      loginActivity={currentTab === 'active' ? loginActivities[company.id] : null}
                      aiChatEnabled={aiChatRows[company.id]?.enabled === true}
                      dealerName={isSuperAdmin ? company.dealerName : undefined}
                      demoUntil={company.demoUntil}
                      demoExpired={company.demoExpired}
                      onViewDetails={() => handleOpenModal(company)}
                      onModules={(e?: React.MouseEvent) => {
                        setModulesCompany({ id: company.id, name: company.name });
                      }}
                      /* Aşağıdaki aksiyonlar süper admine özel — bayi görünümünde menüde çıkmaz */
                      onDelete={isSuperAdmin ? handleDeleteCompany : undefined}
                      onSyncToSql={isSuperAdmin ? handleSyncCompanyToSql : undefined}
                      onCreateContract={isSuperAdmin ? () => handleOpenContractModal(company) : undefined}
                      onFiscalDevices={isSuperAdmin ? () => handleOpenFiscalDeviceModal(company) : undefined}
                      onAiChat={isSuperAdmin ? () => setAiChatCompany({ id: company.id, name: company.name }) : undefined}
                      onAssignDealer={isSuperAdmin ? () => handleOpenAssignDealer(company) : undefined}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : (
            <CompanyListView />
          )}

      {/* Personel Sayısı Güncelleme Modal */}
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Fade in={isModalOpen}>
          <Box sx={ModalStyle}>
            <Box sx={{ 
              p: 2, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              bgcolor: '#1976d2',
              color: 'white'
            }}>
              <Typography variant="h6">
                Personel Sayısı Güncelleme
              </Typography>
              <IconButton 
                size="small" 
                onClick={handleCloseModal}
                sx={{ color: 'white' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            
            <Box sx={{ p: 3 }}>
              {selectedCompany && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar
                      src={selectedCompany.logo}
                      alt={selectedCompany.name}
                      sx={{ width: 50, height: 50, mr: 2, bgcolor: '#1976d2' }}
                    >
                      {selectedCompany.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6">{selectedCompany.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedCompany.admin.name}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    Mevcut Maksimum Personel Sayısı: <strong>{selectedCompany.maxPersonnel || 0}</strong>
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label="Yeni Maksimum Personel Sayısı"
                    variant="outlined"
                    type="number"
                    value={newMaxPersonnel}
                    onChange={(e) => setNewMaxPersonnel(parseInt(e.target.value) || 0)}
                    inputProps={{ min: 0 }}
                    sx={{ mb: 3 }}
                  />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button 
                      variant="outlined" 
                      onClick={handleCloseModal}
                      startIcon={<CloseIcon />}
                    >
                      İptal
                    </Button>
                    <Button 
                      variant="contained" 
                      onClick={handleUpdateMaxPersonnel}
                      disabled={updating}
                      startIcon={<SaveIcon />}
                      sx={{
                        bgcolor: '#1976d2',
                        '&:hover': {
                          bgcolor: '#1565c0'
                        }
                      }}
                    >
                      {updating ? 'Güncelleniyor...' : 'Güncelle'}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Şirket Silme Modal */}
      <Modal
        open={isDeleteModalOpen}
        onClose={closeDeleteModal}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
        disableEscapeKeyDown={isDeleting}
      >
        <Fade in={isDeleteModalOpen}>
          <Box sx={ModalStyle}>
            <Box sx={{ 
              p: 2, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              bgcolor: '#f44336',
              color: 'white'
            }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DeleteIcon /> Şirket Silme
              </Typography>
              {!isDeleting && (
                <IconButton 
                  size="small" 
                  onClick={closeDeleteModal}
                  sx={{ color: 'white' }}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
            
            <Box sx={{ p: 3 }}>
              {isDeleting ? (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Şirket Siliniyor: {companyToDelete?.name}
                  </Typography>
                  <LinearProgress sx={{ mb: 3 }} />
                  
                  <Box sx={{ 
                    maxHeight: 300, 
                    overflowY: 'auto', 
                    border: '1px solid #e0e0e0', 
                    borderRadius: 1,
                    p: 2,
                    mb: 2,
                    bgcolor: '#f5f5f5',
                    fontFamily: 'monospace',
                    fontSize: '0.85rem'
                  }}>
                    {deleteProgress.map((msg, idx) => (
                      <Box key={idx} sx={{ py: 0.5 }}>
                        {msg}
                      </Box>
                    ))}
                  </Box>
                  
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Silme işlemi devam ediyor, lütfen bekleyin...
                  </Alert>
                </Box>
              ) : (
                <>
                  {/* Sözleşme Kontrolü - Sözleşmeli şirketler silinemez */}
                  {companyToDelete && hasActiveContract(companyToDelete.id) ? (
                    <>
                      <Alert severity="error" sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <WarningIcon color="error" /> 
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            Bu Şirket Silinemez!
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Bu şirket ile aktif bir sözleşme bulunmaktadır. Sözleşmeli şirketler silinemez.
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                          Lütfen sözleşmenin bitmesini bekleyiniz veya sözleşmeyi iptal ediniz.
                        </Typography>
                      </Alert>
                      
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: '#fff3e0', 
                        borderRadius: 1, 
                        border: '1px solid #ffb74d',
                        mb: 3
                      }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                          📋 Sözleşme Bilgileri:
                        </Typography>
                        <Typography variant="body2">
                          <strong>Şirket:</strong> {companyToDelete.name}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Başlangıç:</strong> {new Date(contracts[companyToDelete.id].startDate).toLocaleDateString('tr-TR')}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Bitiş:</strong> {new Date(contracts[companyToDelete.id].endDate).toLocaleDateString('tr-TR')}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Kalan Gün:</strong> {contracts[companyToDelete.id].daysRemaining} gün
                        </Typography>
                        {contracts[companyToDelete.id].isExpired && (
                          <Typography variant="body2" sx={{ color: '#f44336', fontWeight: 'bold', mt: 1 }}>
                            ⚠️ Sözleşme süresi dolmuş!
                          </Typography>
                        )}
                      </Box>
                      
                      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        Şirket adını yazma formu devre dışı bırakılmıştır.
                      </Typography>
                      
                      <TextField
                        fullWidth
                        variant="outlined"
                        placeholder={companyToDelete.name}
                        value=""
                        disabled={true}
                        sx={{ mb: 3, bgcolor: '#f5f5f5' }}
                      />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button 
                          variant="outlined" 
                          onClick={closeDeleteModal}
                          startIcon={<CloseIcon />}
                        >
                          Kapat
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Alert severity="warning" sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <WarningIcon color="error" /> 
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            Bu işlem geri alınamaz!
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Şirketi sildiğinizde, tüm şirket verileri kalıcı olarak silinecektir. 
                          Personel hesapları korunacak, ancak şirket bilgileri silinecektir.
                        </Typography>
                      </Alert>
                      
                      {companyToDelete && (
                        <>
                          <Typography variant="h6" gutterBottom>
                            {companyToDelete.name} şirketini silmek istediğinize emin misiniz?
                          </Typography>
                          
                          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                            Onaylamak için şirket adını yazın: <strong>{companyToDelete.name}</strong>
                          </Typography>
                          
                          <TextField
                            fullWidth
                            variant="outlined"
                            placeholder={companyToDelete.name}
                            value={deleteConfirmation}
                            onChange={(e) => setDeleteConfirmation(e.target.value)}
                            error={!!deleteConfirmation && deleteConfirmation !== companyToDelete.name}
                            helperText={deleteConfirmation && deleteConfirmation !== companyToDelete.name ? 'Şirket adı doğru değil' : ''}
                            sx={{ mb: 3 }}
                          />
                          
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button 
                              variant="outlined" 
                              onClick={closeDeleteModal}
                              startIcon={<CloseIcon />}
                            >
                              İptal
                            </Button>
                            <Button 
                              variant="contained" 
                              color="error"
                              onClick={confirmDelete}
                              disabled={deleteConfirmation !== companyToDelete.name}
                              startIcon={<DeleteIcon />}
                            >
                              Şirketi Sil
                            </Button>
                          </Box>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>
      
      {/* Şirket Detayları Modal */}
      <CompanyDetailsModal
        open={isDetailsModalOpen}
        onClose={handleCloseDetailsModal}
        company={detailsCompany}
      />

      {/* Yazarkasa Cihazları Modal */}
      {fiscalDeviceCompany && (
        <FiscalDeviceModal
          open={isFiscalDeviceModalOpen}
          onClose={handleCloseFiscalDeviceModal}
          companyId={fiscalDeviceCompany.id}
          companyName={fiscalDeviceCompany.name}
        />
      )}

      {/* Şirket Sözleşmesi Modal */}
      <Modal
        open={isContractModalOpen}
        onClose={handleCloseContractModal}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Fade in={isContractModalOpen}>
          <Box sx={ModalStyle}>
            <Box sx={{ 
              p: 2, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              bgcolor: '#66bb6a',
              color: 'white'
            }}>
              <Typography variant="h6">
                📄 Şirket Sözleşmesi
              </Typography>
              <IconButton 
                size="small" 
                onClick={handleCloseContractModal}
                sx={{ color: 'white' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            
            <Box sx={{ p: 3 }}>
              {selectedCompanyForContract && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar
                      src={selectedCompanyForContract.logo}
                      alt={selectedCompanyForContract.name}
                      sx={{ width: 50, height: 50, mr: 2, bgcolor: '#66bb6a' }}
                    >
                      {selectedCompanyForContract.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6">{selectedCompanyForContract.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedCompanyForContract.admin.name}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <TextField
                    fullWidth
                    label="Sözleşme Başlangıç Tarihi"
                    type="date"
                    value={contractStartDate}
                    onChange={(e) => setContractStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 2 }}
                  />
                  
                  <TextField
                    fullWidth
                    label="Sözleşme Bitiş Tarihi"
                    type="date"
                    value={contractEndDate}
                    onChange={(e) => setContractEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 3 }}
                  />
                  
                  {contracts[selectedCompanyForContract.id] && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        Mevcut sözleşme: {new Date(contracts[selectedCompanyForContract.id].startDate).toLocaleDateString('tr-TR')} - {new Date(contracts[selectedCompanyForContract.id].endDate).toLocaleDateString('tr-TR')}
                      </Typography>
                      <Typography variant="body2">
                        Kalan gün: <strong>{contracts[selectedCompanyForContract.id].daysRemaining}</strong>
                      </Typography>
                    </Alert>
                  )}
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button 
                      variant="outlined" 
                      onClick={handleCloseContractModal}
                      startIcon={<CloseIcon />}
                    >
                      İptal
                    </Button>
                    <Button 
                      variant="contained" 
                      onClick={handleSaveContract}
                      disabled={savingContract || !contractStartDate || !contractEndDate}
                      startIcon={<SaveIcon />}
                      sx={{
                        bgcolor: '#66bb6a',
                        '&:hover': {
                          bgcolor: '#57a05a'
                        }
                      }}
                    >
                      {savingContract ? 'Kaydediliyor...' : 'Kaydet'}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* SQL Senkronizasyon Snackbar */}
      <Snackbar
        open={syncSnackbar.open}
        autoHideDuration={syncSnackbar.severity === 'info' ? null : 4000}
        onClose={() => setSyncSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSyncSnackbar(prev => ({ ...prev, open: false }))}
          severity={syncSnackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {syncSnackbar.message}
        </Alert>
      </Snackbar>

      {/* AI Chat Asistanı ayarları (aç/kapat + limit + kullanım + konuşma dökümü) */}
      <AiChatSettingsDialog
        open={aiChatCompany !== null}
        onClose={() => setAiChatCompany(null)}
        company={aiChatCompany}
        row={aiChatCompany ? aiChatRows[aiChatCompany.id] || null : null}
        onChanged={fetchAiChatRows}
      />

      {/* Modül yönetimi (şirket bazlı modül aç/kapa) */}
      <ModuleSettingsDialog
        open={modulesCompany !== null}
        onClose={() => setModulesCompany(null)}
        company={modulesCompany}
      />

      {/* Şirket oluşturma (superadmin + bayi) */}
      <CreateCompanyDialog
        open={isCreateCompanyOpen}
        onClose={() => setIsCreateCompanyOpen(false)}
        onCreated={reloadCompanies}
      />

      {/* Bayiye Ata (yalnız süper admin — kart menüsü kısayolu) */}
      <Dialog open={assignDealerCompany !== null} onClose={closeAssignDealer} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: '#7b1fa2', color: 'white', py: 1.5 }}>
          Bayiye Ata — {assignDealerCompany?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          {assignDealerCompany?.dealerName && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Şu anki bayi: <strong>{assignDealerCompany.dealerName}</strong>. Yeni bir bayi seçerseniz atama taşınır.
            </Alert>
          )}
          <Autocomplete
            fullWidth
            size="small"
            loading={dealersLoading}
            options={dealersList.filter((d) => d.id !== assignDealerCompany?.dealerId)}
            getOptionLabel={(d) => d.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={selectedDealer}
            onChange={(_e, value) => setSelectedDealer(value)}
            renderInput={(params) => (
              <TextField {...params} label="Bayi seç..." placeholder="Bayi adı yazın" />
            )}
            noOptionsText={dealersLoading ? 'Yükleniyor...' : 'Bayi bulunamadı'}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          {assignDealerCompany?.dealerId && (
            <Button
              color="error"
              onClick={handleUnassignDealer}
              disabled={assigningDealer}
              sx={{ mr: 'auto' }}
            >
              Atamayı Kaldır
            </Button>
          )}
          <Button onClick={closeAssignDealer} disabled={assigningDealer}>İptal</Button>
          <Button
            variant="contained"
            onClick={handleAssignDealer}
            disabled={!selectedDealer || assigningDealer}
            startIcon={assigningDealer ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Ata
          </Button>
        </DialogActions>
      </Dialog>

      {/* Canlı destek: sayfa açıkken yeni talepler sesli uyarıyla düşer,
          ilk devralan yetkili görüşmeyi üstlenir (SignalR /hubs/support).
          İlk fazda süper admine özel — bayide render edilmez. */}
      {isSuperAdmin && <LiveSupportPanel />}
    </SuperAdminLayout>
  );
};

export default SuperAdmin; 